import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { namespace } from '@common-config/constants';
import type { MyBridgeAgentBoard } from '@common/reducers/backend-api/backend-api';
import { X } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const BUILT_IN = ['tasks', 'leads', 'invoices'] as const;
const OTHER = 'other';
const CONTENT_PROPS = { zIndex: 30000, className: 'min-w-[var(--radix-select-trigger-width)]' };

type Pick = { boardId: string; kind: string };

/**
 * "Can manage boards": the agent's own boards, and "Use an existing board for <kind>" — the picked
 * board becomes the agent's board of that kind on save (backend services/agent-boards adoptBoards),
 * so the agent keeps using it instead of opening a new one.
 */
export function AgentBoardsSection({
  boards,
  accountBoards,
  pending,
  onChange,
  dir,
  classes,
}: {
  boards: MyBridgeAgentBoard[];
  accountBoards: { id: string; name: string }[];
  pending: Pick[];
  onChange: (next: Pick[]) => void;
  dir?: 'ltr' | 'rtl';
  classes: { label: string; hint: string; input: string };
}) {
  const { t } = useTranslation(namespace);
  const [boardId, setBoardId] = useState('');
  const [kind, setKind] = useState<string>('tasks');
  const [otherName, setOtherName] = useState('');

  const kindLabel = (k: string) =>
    (BUILT_IN as readonly string[]).includes(k) ? t(`myBridge.boards.kinds.${k}`) : k.replace(/^custom:/, '').replace(/-/g, ' ');
  const nameOf = (id: string) => accountBoards.find((b) => b.id === id)?.name ?? boards.find((b) => b.id === id)?.name ?? id;
  const chosenKind = kind === OTHER ? otherName.trim() : kind;

  const add = () => {
    if (!boardId || !chosenKind) return;
    onChange([...pending.filter((p) => p.boardId !== boardId), { boardId, kind: chosenKind }]);
    setBoardId('');
    setOtherName('');
  };

  return (
    <div className="space-y-2" data-testid="agent-boards">
      {boards.length > 0 && (
        <div className="space-y-1">
          <Label className={classes.label}>{t('myBridge.boards.its')}</Label>
          <ul className="text-sm text-ai-text space-y-0.5">
            {boards.map((b) => (
              <li key={b.id}>
                {b.name} <span className="text-ai-text-secondary">· {kindLabel(b.kind)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Label className={classes.label}>{t('myBridge.boards.useExisting')}</Label>
      <div className="flex flex-wrap items-center gap-2">
        <Select dir={dir} value={boardId} onValueChange={setBoardId}>
          <SelectTrigger className={`${classes.input} w-56`} data-testid="agent-boards-board"><SelectValue placeholder={t('myBridge.boards.pickBoard')} /></SelectTrigger>
          <SelectContent {...CONTENT_PROPS}>
            {accountBoards.map((b) => <SelectItem key={b.id} value={b.id}>{b.name || b.id}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select dir={dir} value={kind} onValueChange={setKind}>
          <SelectTrigger className={`${classes.input} w-40`} data-testid="agent-boards-kind"><SelectValue /></SelectTrigger>
          <SelectContent {...CONTENT_PROPS}>
            {BUILT_IN.map((k) => <SelectItem key={k} value={k}>{t(`myBridge.boards.kinds.${k}`)}</SelectItem>)}
            <SelectItem value={OTHER}>{t('myBridge.boards.kinds.other')}</SelectItem>
          </SelectContent>
        </Select>
        {kind === OTHER && (
          <Input className={`${classes.input} w-40`} value={otherName} onChange={(e) => setOtherName(e.target.value)} placeholder={t('myBridge.boards.otherPlaceholder')} data-testid="agent-boards-other" />
        )}
        <Button type="button" variant="outline" size="sm" onClick={add} disabled={!boardId || !chosenKind} data-testid="agent-boards-add">
          {t('myBridge.boards.add')}
        </Button>
      </div>

      {pending.length > 0 && (
        <ul className="text-sm space-y-0.5" data-testid="agent-boards-pending">
          {pending.map((p) => (
            <li key={p.boardId} className="flex items-center gap-1">
              {t('myBridge.boards.pendingLine', { board: nameOf(p.boardId), kind: kindLabel(p.kind) })}
              <button type="button" className="text-ai-text-secondary hover:text-ai-text" onClick={() => onChange(pending.filter((x) => x.boardId !== p.boardId))} aria-label={t('myBridge.boards.remove')}>
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className={classes.hint}>{t('myBridge.boards.hint')}</p>
    </div>
  );
}
